import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ReactTooltip from "react-tooltip";
import { AppWrap, MotionWrap } from "../../wrapper";
import { LoadingSpinner } from "../../components";
import { urlFor } from "../../client";
import { useData } from "../../contexts/DataContext";
import "./Experience.scss";
import moment from "moment";

const workTime = (startDate, endDate) => {
  const diffYears = moment(endDate).diff(
    moment(startDate).add(-1, "months"),
    "years"
  );
  const diffMonth = moment(endDate).diff(
    moment(startDate).add(-1, "months"),
    "months"
  );

  if (diffYears > 0) {
    return diffYears + " years " + (diffMonth % 12) + " months";
  } else {
    return diffMonth + " months";
  }
};

const getDateStr = (startDate, endDate) => {
  const relativeTime = workTime(startDate, endDate);
  if (!endDate)
    return (
      moment(startDate).format("MMM YYYY") +
      " - Present " +
      " ● " +
      relativeTime
    );
  return (
    moment(startDate).format("MMM YYYY") +
    " - " +
    moment(endDate).format("MMM YYYY") +
    " ● " +
    relativeTime
  );
};

const Experience = () => {
  const { experiences: rawExperiences, loading, error } = useData();
  const [expandedItems, setExpandedItems] = useState({});

  // Sort experiences by start date
  const experiences = rawExperiences.sort((a, b) => {
    if (a.companyStartDate > b.companyStartDate) return -1;
    if (a.companyStartDate < b.companyStartDate) return 1;
    return 0;
  });

  if (loading) {
    return <LoadingSpinner message="Loading experience..." />;
  }

  if (error) {
    return <div className="error-message">Error loading data: {error}</div>;
  }

  const toggleExpanded = (index) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <div className="app__experience-container z-10">
      <h2 className="head-text">Experience</h2>

      <div className="app__experience-timeline">
        {experiences.map((experience, index) => (
          <motion.div
            className="app__experience-card"
            key={experience._id || index}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            viewport={{ once: true }}
          >
            <div className="app__experience-timeline-marker">
              <div className="app__experience-dot"></div>
              <div className="app__experience-line"></div>
            </div>

            <div className="app__experience-content">
              <div className="app__experience-header">
                <div className="app__experience-company-logo">
                  <img
                    src={urlFor(experience.companyLogo)}
                    alt={experience.companyName}
                    data-tip={experience.companyName}
                  />
                </div>

                <div className="app__experience-main-info">
                  <h3 className="app__experience-company-name">
                    {experience.companyName}
                  </h3>
                  <h4 className="app__experience-position">
                    {experience.companyPosition}
                  </h4>
                  <p className="app__experience-duration">
                    {getDateStr(
                      experience.companyStartDate,
                      experience.companyEndDate
                    )}
                  </p>
                </div>

                <button
                  className="app__experience-expand-btn"
                  onClick={() => toggleExpanded(index)}
                  aria-label={
                    expandedItems[index] ? "Collapse details" : "Expand details"
                  }
                >
                  <motion.div
                    animate={{ rotate: expandedItems[index] ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M7 10L12 15L17 10"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </motion.div>
                </button>
              </div>

              <AnimatePresence>
                {expandedItems[index] && (
                  <motion.div
                    className="app__experience-details"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    <div className="app__experience-description">
                      <h5>Description</h5>
                      <pre className="app__experience-desc-text">
                        {experience.companyDescription}
                      </pre>
                    </div>

                    <div className="app__experience-skills">
                      <h5>Technologies & Skills</h5>
                      <div className="app__experience-tech-chips">
                        {experience.companyTech.map((tech, i) => (
                          <span
                            className="app__experience-tech-chip"
                            key={`${tech}-${i}`}
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        ))}
      </div>
      <ReactTooltip place="top" type="dark" effect="solid" />
    </div>
  );
};

export default AppWrap(
  MotionWrap(Experience, "app__experience"),
  "experience",
  "app__whitebg"
);
